import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import type { BoardDto, ProjectWorkspaceDto, TaskSummaryDto } from '@taqon/contracts';
import { daysBetween } from '../../../shared/documents/dates';
import { formatEstimate } from './api';
import { Assignees, LabelChip, PriorityChip } from './parts';

type SortKey = 'key' | 'title' | 'status' | 'priority' | 'due';
const PRIORITY_ORDER = { urgent: 0, high: 1, medium: 2, low: 3 } as const;

/**
 * The board's tasks as a table (CLR-66): the "spreadsheet" view. Subtasks sit indented under their
 * parent. The status and the due date can be changed in the row.
 */
export function TaskList({
  board,
  tasks,
  workspace,
  today,
  onOpen,
  onStatus,
  onDueDate,
}: {
  board: BoardDto;
  tasks: readonly TaskSummaryDto[];
  workspace: ProjectWorkspaceDto;
  today: string;
  onOpen: (taskId: string) => void;
  onStatus: (task: TaskSummaryDto, columnId: string) => void;
  onDueDate: (task: TaskSummaryDto, dueDate: string | null) => void;
}) {
  const [sort, setSort] = useState<{ key: SortKey; ascending: boolean }>({
    key: 'key',
    ascending: true,
  });
  const columns = [...board.columns].sort((a, b) => a.sequence - b.sequence);
  const sequence = new Map(columns.map((c) => [c.id, c.sequence]));
  const compare = (a: TaskSummaryDto, b: TaskSummaryDto): number => {
    const number = (task: TaskSummaryDto) => Number(task.key.split('-').pop() ?? 0);
    const by: Record<SortKey, number> = {
      key: number(a) - number(b),
      title: a.title.localeCompare(b.title),
      status: (sequence.get(a.columnId) ?? 0) - (sequence.get(b.columnId) ?? 0),
      priority: PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority],
      due: (a.dueDate ?? '9999-12-31').localeCompare(b.dueDate ?? '9999-12-31'),
    };
    return (sort.ascending ? 1 : -1) * (by[sort.key] || number(a) - number(b));
  };
  const parents = tasks.filter((t) => t.parentTaskId === null).sort(compare);
  const children = (id: string) => tasks.filter((t) => t.parentTaskId === id).sort(compare);
  // A subtask whose parent is filtered out is still listed, at the top level.
  const shownParents = new Set(parents.map((p) => p.id));
  const orphans = tasks
    .filter((t) => t.parentTaskId !== null && !shownParents.has(t.parentTaskId))
    .sort(compare);
  const rows = [
    ...parents.flatMap((parent) => [
      { task: parent, depth: 0 },
      ...children(parent.id).map((task) => ({ task, depth: 1 })),
    ]),
    ...orphans.map((task) => ({ task, depth: 0 })),
  ];

  const header = (key: SortKey, label: string) => (
    <TableCell sortDirection={sort.key === key ? (sort.ascending ? 'asc' : 'desc') : false}>
      <TableSortLabel
        active={sort.key === key}
        direction={sort.key === key && !sort.ascending ? 'desc' : 'asc'}
        onClick={() => {
          setSort((current) => ({
            key,
            ascending: current.key === key ? !current.ascending : true,
          }));
        }}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Table size="small" aria-label={`Tasks of ${board.name}`}>
      <TableHead>
        <TableRow>
          {header('key', 'Key')}
          {header('title', 'Task')}
          {header('status', 'Status')}
          {header('priority', 'Priority')}
          <TableCell>Assignees</TableCell>
          <TableCell>Labels</TableCell>
          {header('due', 'Due')}
          <TableCell>Estimate</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map(({ task, depth }) => {
          const overdue =
            task.category !== 'done' &&
            task.dueDate !== null &&
            daysBetween(today, task.dueDate) < 0;
          return (
            <TableRow key={task.id} hover>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{task.key}</TableCell>
              <TableCell sx={{ pl: depth === 0 ? 2 : 5 }}>
                <Link
                  component="button"
                  type="button"
                  onClick={() => {
                    onOpen(task.id);
                  }}
                  sx={{ textAlign: 'left', fontWeight: depth === 0 ? 600 : 400 }}
                >
                  {depth > 0 ? '↳ ' : ''}
                  {task.title}
                </Link>
              </TableCell>
              <TableCell sx={{ minWidth: 150 }}>
                <TextField
                  select
                  size="small"
                  fullWidth
                  value={task.columnId}
                  disabled={!workspace.canEdit}
                  slotProps={{
                    select: { SelectDisplayProps: { 'aria-label': `Status of ${task.key}` } },
                  }}
                  onChange={(event) => {
                    onStatus(task, event.target.value);
                  }}
                >
                  {columns.map((column) => (
                    <MenuItem key={column.id} value={column.id}>
                      {column.name}
                    </MenuItem>
                  ))}
                </TextField>
              </TableCell>
              <TableCell>
                <PriorityChip priority={task.priority} />
              </TableCell>
              <TableCell>
                <Assignees ids={task.assigneeIds} members={workspace.members} size={22} />
              </TableCell>
              <TableCell>
                <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                  {workspace.labels
                    .filter((l) => task.labelIds.includes(l.id))
                    .map((label) => (
                      <LabelChip key={label.id} label={label} />
                    ))}
                </Stack>
              </TableCell>
              <TableCell sx={{ minWidth: 150 }}>
                <TextField
                  type="date"
                  size="small"
                  value={task.dueDate ?? ''}
                  disabled={!workspace.canEdit}
                  error={overdue}
                  slotProps={{ htmlInput: { 'aria-label': `Due date of ${task.key}` } }}
                  onChange={(event) => {
                    onDueDate(task, event.target.value === '' ? null : event.target.value);
                  }}
                />
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                {formatEstimate(task.estimateMinutes) ?? '—'}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** `YYYY-MM` of a business date. */
export function monthOf(date: string): string {
  return date.slice(0, 7);
}

/** The month before or after `YYYY-MM`. */
export function shiftMonth(month: string, by: number): string {
  const [y = 2000, m = 1] = month.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1 + by, 1));
  return `${String(date.getUTCFullYear())}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** The weeks (Monday first) covering the month, as `YYYY-MM-DD` or null outside it. */
export function monthGrid(month: string): (string | null)[][] {
  const [y = 2000, m = 1] = month.split('-').map(Number);
  const first = new Date(Date.UTC(y, m - 1, 1));
  const days = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const lead = (first.getUTCDay() + 6) % 7;
  const cells: (string | null)[] = [
    ...Array.from({ length: lead }, () => null),
    ...Array.from({ length: days }, (_, i) => `${month}-${String(i + 1).padStart(2, '0')}`),
  ];
  while (cells.length % 7 !== 0) cells.push(null);
  return Array.from({ length: cells.length / 7 }, (_, w) => cells.slice(w * 7, w * 7 + 7));
}

/** The tasks by due date on a month grid (CLR-66); a task opens from its day. */
export function TaskCalendar({
  tasks,
  month,
  today,
  onMonth,
  onOpen,
}: {
  tasks: readonly TaskSummaryDto[];
  month: string;
  today: string;
  onMonth: (month: string) => void;
  onOpen: (taskId: string) => void;
}) {
  const title = new Intl.DateTimeFormat('en-GB', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${month}-01T00:00:00Z`));
  const undated = tasks.filter((t) => t.dueDate === null).length;
  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1.5 }}>
        <Button
          size="small"
          aria-label="Previous month"
          onClick={() => {
            onMonth(shiftMonth(month, -1));
          }}
        >
          <ChevronLeftIcon />
        </Button>
        <Typography variant="subtitle1" component="h3" sx={{ minWidth: 160, textAlign: 'center' }}>
          {title}
        </Typography>
        <Button
          size="small"
          aria-label="Next month"
          onClick={() => {
            onMonth(shiftMonth(month, 1));
          }}
        >
          <ChevronRightIcon />
        </Button>
        <Button
          size="small"
          onClick={() => {
            onMonth(monthOf(today));
          }}
        >
          This month
        </Button>
        {undated > 0 && (
          <Typography variant="caption" color="text.secondary">
            {undated} {undated === 1 ? 'task has' : 'tasks have'} no due date
          </Typography>
        )}
      </Stack>
      <Box
        role="grid"
        aria-label={`Tasks due in ${title}`}
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, minmax(110px, 1fr))',
          gap: 0.5,
          overflowX: 'auto',
        }}
      >
        {WEEKDAYS.map((day) => (
          <Typography
            key={day}
            role="columnheader"
            variant="caption"
            sx={{ fontWeight: 700, color: 'text.secondary', px: 0.5 }}
          >
            {day}
          </Typography>
        ))}
        {monthGrid(month)
          .flat()
          .map((date, index) => {
            const due = date === null ? [] : tasks.filter((t) => t.dueDate === date);
            return (
              <Box
                key={date ?? `blank-${String(index)}`}
                role="gridcell"
                aria-label={date ?? undefined}
                sx={{
                  minHeight: 96,
                  p: 0.5,
                  borderRadius: 1,
                  border: 1,
                  borderColor: date === today ? 'primary.main' : 'divider',
                  bgcolor: date === null ? 'transparent' : 'background.paper',
                }}
              >
                {date !== null && (
                  <Typography variant="caption" sx={{ fontWeight: date === today ? 700 : 400 }}>
                    {Number(date.slice(8))}
                  </Typography>
                )}
                <Stack spacing={0.25}>
                  {due.map((task) => {
                    const overdue = task.category !== 'done' && date !== null && date < today;
                    return (
                      <ButtonBase
                        key={task.id}
                        onClick={() => {
                          onOpen(task.id);
                        }}
                        aria-label={`Open ${task.key} ${task.title}`}
                        sx={{
                          justifyContent: 'flex-start',
                          textAlign: 'left',
                          px: 0.5,
                          py: 0.25,
                          borderRadius: 0.75,
                          fontSize: '0.75rem',
                          borderLeft: 3,
                          borderColor: overdue ? 'error.main' : 'primary.main',
                          bgcolor: 'grey.50',
                          textDecoration: task.category === 'done' ? 'line-through' : 'none',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {task.key} {task.title}
                      </ButtonBase>
                    );
                  })}
                </Stack>
              </Box>
            );
          })}
      </Box>
    </Box>
  );
}
